import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Button, Row } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
  getAccessToken,
  setPageTitle,
  logIn,
  tryLoginWithLastName,
} from '../actions';
import { FormInput, ErrorOverlay, Header, HeaderIcon } from '../components';
import normalization from 'cdfdigital-common/build/helper/normalization';
import validate from 'cdfdigital-common/build/helper/validation';
import detectCurrentLabel from '../helper/label';
import { AccessTokenState } from 'cdfdigital-common/src/models';
import { authInfoActions } from '../reducers/authInfoReducer';
import { accessTokenActions } from '../reducers/accessTokenReducer';
import { loginActions } from '../reducers/loginReducer';
import { LoginState } from 'cdfdigital-common/src/models/Login';

type AuthInfoState = {
  data: {
    identifier: string;
    email: string;
    recaptchaToken?: string;
    lastName: string;
  };
};

const Login: React.FC = () => {
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const history = useHistory();
  const dispatch = useDispatch();
  const [tokenError, setTokenError] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const { data: authInfo } = useSelector<any, AuthInfoState>(
    (state) => state.authInfoReducer,
  );

  const accessToken = useSelector<any, AccessTokenState>(
    (state) => state.accessTokenReducer,
  );

  const loginState = useSelector<any, LoginState>(
    (state) => state.loginReducer,
  );

  const initialValues = {
    identifier: normalization.cpf(authInfo.identifier),
    email: '',
  };

  /*const labelsWithLastName = {
    tokiomarine: 'tokiomarine',
    pernambucanas: 'pernambucanas',
  };*/

  const isLoginLastName = (): boolean => {
    const labelsWithLastName = ['tokiomarine', 'pernambucanas'];
    return labelsWithLastName.includes(detectCurrentLabel());
  };

  /*
  const getTextSendButton = (): string => {
    const textSendButton = isLoginLastName() ? 'Login' : 'Entrar';
    return textSendButton;
  };*/

  //const previousPincode = authInfo.pincode; // previously selected option

  // useEffect(() => {
  //   if (initialValues.identifier) {
  //     setState({ ...state, pincode: previousPincode });
  //   }
  // }, [state, initialValues, previousPincode]);

  useEffect(() => {
    (window as any).onRecaptchaSuccess = (token: string) => {
      setRecaptchaToken(token);
    };

    (window as any).onRecaptchaExpired = () => {
      setRecaptchaToken(null);
    };
  }, []);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);
  useEffect(() => {
    dispatch(loginActions.resetStatus()); // Reseta erro ao entrar na tela
  }, [dispatch]);

  useEffect(() => {
    // Clear state on page load to avoid using same returned data after logoff
    dispatch(accessTokenActions.clearState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(setPageTitle('Criar Código de Acesso'));
    const token = localStorage.getItem('token');
    if (token) {
      dispatch(logIn({ token }));
      history.push('main'); // FIXME test code
    }
  }, [dispatch, history]);

  useEffect(() => {
    if (accessToken.error || loginState.error) {
      setTokenError(true);
    }
  }, [accessToken, loginState]);

  useEffect(() => {
    if (
      hasSubmitted &&
      !accessToken.error &&
      accessToken.data?.signature &&
      !isLoginLastName()
    ) {
      history.push('access-token');
    }
  }, [accessToken, hasSubmitted, history]);

  useEffect(() => {
    if (loginState.data.token) {
      history.push('/main/form');
      dispatch(loginActions.resetStatus());
    }
  }, [hasSubmitted, loginState, history, dispatch]);

  // const onSubmit = async (values: any) => {
  //   if (!recaptchaToken) {
  //     alert('Por favor, resolva o reCAPTCHA.');
  //     return;
  //   }

  //   setHasSubmitted(true);

  //   const { identifier, email, lastName } = values;

  //   const state: AuthInfoState = {
  //     data: {
  //       identifier: identifier.replace(/\D/g, ''),
  //       email: '',
  //       recaptchaToken,
  //       lastName,
  //     },
  //   };

  //   dispatch(authInfoActions.setData(state));
  //   if (isLoginLastName()) {
  //     dispatch(loginActions.resetStatus());
  //     return dispatch(
  //       tryLoginWithLastName({
  //         identifier: identifier.replace(/\D/g, ''),
  //         lastName,
  //       }),
  //     );
  //   }

  //   dispatch(getAccessToken(state));
  // };

  const onSubmit = async (values: any) => {
    // Verifica se o objeto grecaptcha está disponível no window
    if (!(window as any).grecaptcha) {
      alert(
        'Erro de carregamento do reCAPTCHA. Por favor, recarregue a página.',
      );
      return;
    }

    try {
      // Executa o reCAPTCHA V3 e obtém o token
      const token = await (window as any).grecaptcha.execute(
        process.env.REACT_APP_RECAPTCHA_SITE_KEY,
        { action: 'login' },
      );

      if (!token) {
        alert('Falha na validação de segurança.');
        return;
      }

      setHasSubmitted(true);

      const { identifier, lastName } = values;

      const state: AuthInfoState = {
        data: {
          identifier: identifier.replace(/\D/g, ''),
          email: '',
          recaptchaToken: token, // Injetamos o token gerado aqui
          lastName,
        },
      };

      dispatch(authInfoActions.setData(state));

      if (isLoginLastName()) {
        dispatch(loginActions.resetStatus());
        return dispatch(
          tryLoginWithLastName({
            identifier: identifier.replace(/\D/g, ''),
            lastName,
          }),
        );
      }

      dispatch(getAccessToken(state));
    } catch (error) {
      console.error('Erro reCAPTCHA:', error);
      alert('Erro ao validar acesso. Tente novamente.');
    }
  };

  const getErrorMessage = () => {
    if (accessToken.error) {
      return accessToken.error.message;
    } else if (loginState.error) {
      return loginState.error.message;
    }

    return '';
  };

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    clearErrors,
  } = useForm<any>({
    defaultValues: initialValues,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const emailErrorOverride =
    errors.email && detectCurrentLabel() === 'pernambucanas'
      ? {
          message:
            'Login inválido! Entre em contato no 0800 718 9007 para atualizar seu cadastro',
        }
      : errors.email;

  const lastNameErrorOverride =
    errors.lastName && isLoginLastName()
      ? {
          message:
            'Útilmo sobrenome inválido! Entre em contato no 0800 718 9007 para atualizar seu cadastro',
        }
      : errors.lastName;

  return (
    <div id="container-access-token">
      <Header /*title='Criar Código de Acesso'*/ />
      <div className="container-bg-fluid">
        <div className="container-bg-left">
          <div className="container-bg-menu">
            <div className="icon-container">
              <HeaderIcon name={'account-outline'} />
            </div>
          </div>
        </div>
        <div className="container-bg-right">
          <Row className="d-flex">
            <Col
              xs={12}
              sm={10}
              md={{ span: 8, offset: 2 }}
              className="container-bg-content"
            >
              <form onSubmit={handleSubmit(onSubmit)}>
                <h2 className="page-title">Acesso</h2>
                <h4 className="page-subtitle">
                  {detectCurrentLabel() === 'pernambucanas'
                    ? 'O ACESSO POR CPF PODE SER FEITO A PARTIR DO DIA SEGUINTE À COMPRA DO PRODUTO.'
                    : 'DIGITE O CPF DO TITULAR DA COMPRA DO SERVIÇO PARA INICIAR'}
                </h4>

                <div className="form-group">
                  <Controller
                    name="identifier"
                    control={control}
                    rules={{
                      validate: validate.cpfLength,
                    }}
                    render={({ field }) => (
                      <FormInput
                        input={field}
                        name="identifier"
                        placeholder="CPF"
                        maxLength={14}
                        className="field-required form-control"
                        meta={{
                          error: errors.identifier,
                          touched: false,
                          submitFailed: '',
                        }}
                        normalize={normalization.cpf}
                        clearErrors={clearErrors}
                        setValue={setValue}
                      />
                    )}
                  />
                </div>
                {detectCurrentLabel() !== 'tokiomarine' &&
                detectCurrentLabel() !== 'pernambucanas' ? (
                  <div className="form-group">
                    <Controller
                      name="email"
                      control={control}
                      rules={{ validate: validate.email }}
                      render={({ field }) => (
                        <FormInput
                          input={field}
                          name="email"
                          placeholder="Email"
                          className="field-required form-control"
                          meta={{
                            error: emailErrorOverride,
                            touched: false,
                            submitFailed: '',
                          }}
                          maxLength={55}
                          clearErrors={clearErrors}
                          setValue={setValue}
                        />
                      )}
                    />
                  </div>
                ) : (
                  <div className="form-group">
                    <span>Exemplo: José João da Silva</span>
                    <br />
                    <span>
                      Seu útilmo sobrenome será: <b>SILVA</b>
                    </span>

                    <Controller
                      name="lastName"
                      control={control}
                      rules={{ validate: validate.notEmpty }}
                      render={({ field }) => (
                        <FormInput
                          input={field}
                          name="lastName"
                          placeholder="sobrenome"
                          className="field-required form-control"
                          meta={{
                            error: lastNameErrorOverride,
                            touched: false,
                            submitFailed: '',
                          }}
                          clearErrors={clearErrors}
                          setValue={setValue}
                        />
                      )}
                    />
                  </div>
                )}
                {/* <div
                  className="g-recaptcha"
                  data-sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY ?? ''}
                  data-callback="onRecaptchaSuccess"
                  data-expired-callback="onRecaptchaExpired"
                ></div> */}
                <Button
                  type="submit"
                  className="btn btn-primary"
                  disabled={accessToken.fetching}
                >
                  {accessToken.fetching
                    ? 'Enviando Código de Acesso...'
                    : 'Entrar'}
                </Button>
              </form>
            </Col>
            {(detectCurrentLabel() === 'ticket' ||
              detectCurrentLabel() === 'tokiomarine') && (
              <Col
                xs={12}
                sm={10}
                md={{ span: 8, offset: 2 }}
                className="container-bg-content mt-link"
              >
                <h6>
                  Confira a nossa{' '}
                  <a
                    href="https://politica-de-privacidade.cdf.net/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Política de Privacidade
                  </a>
                  .
                </h6>
              </Col>
            )}
          </Row>
        </div>
      </div>
      <ErrorOverlay
        visible={tokenError}
        onClose={() => setTokenError(false)}
        buttonTitle="Tentar Novamente"
        title="Erro"
        message={getErrorMessage()}
      />
    </div>
  );
};

export default Login;
